/**
 * B355 (checkout-widget leg) — the embedded widget's ONE card
 * (redline-checkout-widget-390.md, 58:238): the tickets step and the pay
 * step FUSED — day → departure → counts → PAY, no step navigation, the
 * whole purchase on a page we do not own. DENSER registers than the
 * storefront throughout, in-flow PAY (we do not own the host viewport,
 * so nothing is sticky), and the Total stays quiet (18, never the
 * storefront's display 28).
 *
 * Honesty seams, each from a read or absent (§10):
 *  - the product meta line renders only when the catalog presentation
 *    carries one; the host page's own heading is never ours to render;
 *  - seats-left, low-amber and sold-out ride the availability read
 *    exactly as the tickets step's (one derivation, shared);
 *  - the Test-mode trust clause renders ONLY while the channel's
 *    publishable key is a TEST key; Secure-checkout only while the
 *    provider's tokenised element is the thing on the card; Powered-by
 *    always (the B355 footer sentence, here inside the card);
 *  - the hold sentence appears only over a live hold.
 *
 * Recorded deviations (B355's line):
 *  - an email field the frame does not draw (tickets need a destination;
 *    dropping it silently would lose the buyer's receipt);
 *  - once seats are HELD (an attempt was made) the day/departure pickers
 *    wall with their why and a Start-over door — a held sailing cannot
 *    be silently swapped, and a trapped buyer is worse than a ghost door
 *    (R14);
 *  - B391's stepper marks rule applies (the low mark stays reachable on
 *    cart lines via the container's wiring).
 */
import type { TimeFormat } from "@transact/ui";
import { clockWords } from "../lib/when.js";
import type { ReactElement } from "react";
import { Banner, Button, Checkbox, Icon, Input, LabelledRule, PaymentWait, PickerRail, PickerTile, SheetCard, Stepper as UiStepper, cap, sizedPhotoUrl } from "@transact/ui";
import { formatMoney, type Money } from "../lib/money.js";
import { groupDepartures, seatsLeft, type DisplayOptions } from "./departures.js";
import type { PoolAvailability, ProductSummary, SlotSummary } from "./screens.js";
import {
  compositionIssues,
  compositionLines,
  issueHint,
  maxQuantity,
  quantityOf,
  totalQuantity,
  type Composition,
} from "../lib/composition.js";
import { cardMeta, type BuyerVocabulary } from "../lib/bookingFace.js";
import { needsDepartureStep } from "../lib/browse.js";
import { displayTotal } from "./ticketsStep.js";
import { holdWords } from "../lib/cartPayFace.js";

const LOW_SEATS = 5;

/**
 * B296/B285 — ONE statement of why PAY is shut. Exported for B511: the
 * wallet row's press must refuse on the SAME sentence (the sheet never
 * opens over a card that cannot yet be paid for).
 */
export function widgetPayWall(input: {
  readonly card: ProductSummary;
  readonly comp: Composition;
  readonly chosenSlotId?: string | undefined;
  readonly gatewayReady: boolean;
  readonly gatewayNotice?: string | undefined;
  readonly vocab?: BuyerVocabulary | undefined;
}): string | undefined {
  const scheduled = needsDepartureStep({ productId: input.card.productId, scheduleIds: input.card.scheduleIds });
  const missing: string[] = [];
  if (totalQuantity(input.comp) === 0) missing.push("tickets");
  if (scheduled && input.chosenSlotId === undefined)
    /* B403 #39-b — buyer sentence (#22-extension): the tenant's word. */
    missing.push(`a ${input.vocab?.departureNoun.singular ?? "departure"}`);
  return compositionIssues(input.card, input.comp).length > 0
    ? "Adjust your tickets to pay."
    : missing.length > 0
      ? `Pick ${missing.join(" and ")} to pay.`
      : !input.gatewayReady
        ? (input.gatewayNotice ??
          "Card payment isn’t set up on this booking page yet. Ask the seller how to pay — nothing has been charged.")
        : undefined;
}

export function WidgetView(props: {
  readonly card: ProductSummary;
  readonly slots: readonly SlotSummary[];
  readonly availability: Readonly<Record<string, readonly PoolAvailability[]>>;
  readonly comp: Composition;
  readonly onChange: (variantId: string, quantity: number) => void;
  readonly chosenSlotId?: string | undefined;
  readonly onPickSlot: (slotId: string) => void;
  readonly selectedDayKey?: string | undefined;
  readonly onSelectDay: (key: string) => void;
  /** The kernel's checkout total once known; else the display derivation quotes. */
  readonly total?: Money | undefined;
  /** B415: NAME rides the email deviation — required, above email; PHONE stays storefront-only. */
  readonly buyerName: string;
  readonly onBuyerName: (value: string) => void;
  /** B415: press-refusal for a missing name — rests quiet, arrives on PRESS. */
  readonly nameRefusal?: string | undefined;
  readonly buyerEmail: string;
  readonly onBuyerEmail: (value: string) => void;
  /** B511: the email the wallet did not share — asked for at the field, before any order. */
  readonly emailRefusal?: string | undefined;
  /**
   * B511 (356-2): the wallets this device can serve (the Express Checkout
   * Element's `ready` read). Absent/empty → no divider, the card path alone.
   * The buttons themselves are the wallets' own, mounted into
   * #express-checkout-element — never drawn here.
   */
  readonly wallets?: readonly string[] | undefined;
  readonly gatewayReady: boolean;
  readonly gatewayNotice?: string | undefined;
  readonly authorized?: boolean;
  readonly paying: boolean;
  readonly onPay: () => void;
  /** pk_test on the channel's publishable key — the trust clause's read. */
  readonly testMode: boolean;
  /** B615 leg 2 — the picked product's acknowledgement line; absent = no box (the cart view's twin). */
  readonly acknowledgement?: string | undefined;
  readonly acknowledged?: boolean;
  readonly onAcknowledged?: (ticked: boolean) => void;
  readonly acknowledgeRefusal?: string | undefined;
  /** B616 leg 3a — the Insider opt-in (cartPay's OptInBox twin); absent = not asked. */
  readonly optIn?: string | undefined;
  readonly optedIn?: boolean;
  readonly onOptIn?: (ticked: boolean) => void;
  readonly holdMsLeft?: number | undefined;
  /** An attempt was made (seats held / order pending): sailing walls shut. */
  readonly locked: boolean;
  readonly onStartOver?: (() => void) | undefined;
  /**
   * The way back to the catalog, when the buyer PICKED this product from the
   * embed's own list (a host that preselects one product has no list to go
   * back to, and passes nothing). 58:238 drew the card alone on a host page
   * and so drew no door; an embed showing several products then had a card
   * with no way out (Navy Pier demo, 2026-09-22). Retires once seats are held
   * — Start over is the door then (a held sailing is not silently left).
   */
  readonly onBack?: (() => void) | undefined;
  /** The catalog's own word for the way back ("All tickets"), when the tenant has one. */
  readonly backLabel?: string | undefined;
  readonly locale?: string | undefined;
  readonly timeZone?: string | undefined;
  /** B621 — the tenant's clock; absent = 12h AM/PM, the ruled default. */
  readonly timeFormat?: TimeFormat | undefined;
  /** B403 (#39): the tenant's buyer vocabulary; absent keeps the standing register. */
  readonly vocab?: BuyerVocabulary | undefined;
}): ReactElement {
  const opts: DisplayOptions = {
    ...(props.locale !== undefined ? { locale: props.locale } : {}),
    ...(props.timeZone !== undefined ? { timeZone: props.timeZone } : {}),
    ...(props.timeFormat !== undefined ? { timeFormat: props.timeFormat } : {}),
  };
  const scheduled = needsDepartureStep({ productId: props.card.productId, scheduleIds: props.card.scheduleIds });
  const groups = groupDepartures(props.slots, opts);
  const selectedDay = groups.find((g) => g.key === props.selectedDayKey) ?? groups[0];
  /* B403 wave-2 residual — 58:238 draws the same 24h register as 72:2. */
  const timeFmt = { format: (ms: number) => clockWords(ms, opts) };
  const meta = cardMeta(props.card);
  const issues = compositionIssues(props.card, props.comp);
  const ticketCount = totalQuantity(props.comp);
  const total = props.total ?? displayTotal(props.card, props.comp);
  const lines = compositionLines(props.card, props.comp);
  const hold = props.holdMsLeft === undefined ? undefined : holdWords(props.holdMsLeft);

  /* B296/B285 — ONE statement of why PAY is shut (shared with the wallet row, B511). */
  const payWall = widgetPayWall({
    card: props.card,
    comp: props.comp,
    chosenSlotId: props.chosenSlotId,
    gatewayReady: props.gatewayReady,
    gatewayNotice: props.gatewayNotice,
    vocab: props.vocab,
  });

  return (
    <SheetCard className="widget-card" redline="widget-card">
      <header className="widget-head">
        {props.onBack !== undefined && !props.locked ? (
          <p className="widget-back">
            <Button variant="textlink-accent" onClick={props.onBack}>
              {`‹ ${props.backLabel ?? "All tickets"}`}
            </Button>
          </p>
        ) : null}
        {/* B521 — the widget's head wears the product's first photo and first sentence. */}
        {props.card.presentation?.photos?.[0] !== undefined ? (
          <img className="widget-photo" src={sizedPhotoUrl(props.card.presentation.photos[0].url, 480)} alt={props.card.presentation.photos[0].alt ?? ""} />
        ) : null}
        <h2 className="widget-title">{props.card.name}</h2>
        {meta !== undefined ? <p className="widget-meta">{meta}</p> : null}
        {props.card.presentation?.summary !== undefined ? <p className="widget-lede">{props.card.presentation.summary}</p> : null}
      </header>

      {scheduled ? (
        <>
          <section aria-labelledby="widget-day-label">
            <p className="widget-label" id="widget-day-label" data-redline="widget-day-label">
              Travel day
            </p>
            {props.locked ? (
              <p className="widget-locked" role="note" id="widget-locked-why">
                {/* B941 — the tenant's buyer word; absent keeps the standing register (the file's convention, B403 #39) */}
                {`Your seats are held for this ${props.vocab?.departureNoun.singular ?? "sailing"} — start over to change it.`}
              </p>
            ) : (
              <PickerRail describedBy="widget-day-label" window>
                {groups.map((day) => (
                  <li key={day.key}>
                    <PickerTile
                      kicker={day.weekday}
                      value={day.dayOfMonth}
                      label={day.label}
                      pressed={day.key === selectedDay?.key}
                      onPress={() => props.onSelectDay(day.key)}
                    />
                  </li>
                ))}
              </PickerRail>
            )}
          </section>

          {props.locked ? null : (
            <section aria-labelledby="widget-departure-label">
              {/* B403 (#39): the tenant's word for a departure. */}
              <p className="widget-label" id="widget-departure-label">
                {props.vocab === undefined ? "Departure" : cap(props.vocab.departureNoun.singular)}
              </p>
              <PickerRail describedBy="widget-departure-label" window>
                {(selectedDay?.departures ?? []).map((d) => {
                  const left = seatsLeft(props.availability[d.slotId] ?? []);
                  const soldOut = left !== undefined && left <= 0;
                  const low = left !== undefined && left > 0 && left <= LOW_SEATS;
                  return (
                    <li key={d.slotId}>
                      <PickerTile
                        value={timeFmt.format(d.start)}
                        {...(left !== undefined
                          ? soldOut
                            ? { note: "Sold out", noteTone: "low" as const }
                            : { note: `${left} left`, ...(low ? { noteTone: "warn" as const } : {}) }
                          : {})}
                        pressed={d.slotId === props.chosenSlotId}
                        disabled={soldOut}
                        onPress={() => props.onPickSlot(d.slotId)}
                      />
                    </li>
                  );
                })}
              </PickerRail>
            </section>
          )}
        </>
      ) : null}

      <section aria-labelledby="widget-tickets-label" data-redline="widget-tickets">
        <p className="widget-label" id="widget-tickets-label">
          Tickets
        </p>
        <ul className="widget-rows">
          {props.card.variants.map((v) => (
            <li key={v.variantId} className="widget-row">
              <span className="widget-row-name">{v.name}</span>
              {v.displayPrice !== undefined ? (
                <span className="widget-row-price">{formatMoney(v.displayPrice)}</span>
              ) : null}
              <UiStepper
                frame="widget"
                label={v.name}
                value={quantityOf(props.comp, v.variantId)}
                min={0}
                max={maxQuantity(props.card, v.variantId)}
                onChange={(next) => props.onChange(v.variantId, next)}
                decreaseLabel={`Fewer ${v.name}`}
                increaseLabel={`More ${v.name}`}
              />
            </li>
          ))}
        </ul>
      </section>

      {issues.length > 0 ? (
        <div id="widget-issues">
          {issues.map((issue) => (
            <p key={`${issue.kind}:${issue.variantId}`} className="hint" role="status">
              {issueHint(issue, props.card)}
            </p>
          ))}
        </div>
      ) : null}

      {lines.length > 0 ? (
        <section className="widget-summary" data-redline="widget-summary" aria-label="summary">
          {lines.map((line) => (
            <p key={line.variantId} className="widget-summary-row">
              <span className="widget-summary-label">
                {/* B403 (58:238, the copy wall): the row drops the type's
                    parenthetical qualifier — the stepper row above already
                    states it. */}
                {line.quantity} × {line.name.replace(/\s*\(.*\)\s*$/u, "").trim()}
              </span>
              {line.linePrice !== undefined ? (
                <span className="widget-summary-amount">{formatMoney(line.linePrice)}</span>
              ) : null}
            </p>
          ))}
          <p className="widget-total" data-redline="widget-total">
            <span className="widget-total-label">Total</span>
            {total !== undefined ? <span className="widget-total-amount">{formatMoney(total)}</span> : null}
          </p>
          {hold !== undefined ? (
            <p className="widget-hold" role="status">
              {hold}
            </p>
          ) : null}
        </section>
      ) : null}

      {props.authorized === true ? (
        /* The WAIT face (Pathik 2026-10-13): the buyer's story walks — Processing → Hang tight → Creating your tickets — over breathing dots; the poller upstairs moves us the moment the kernel does. */
        <PaymentWait redline="widget-pay-wait" />
      ) : (
        <div className="widget-pay" data-redline="widget-pay">
          {/* B511 (356-2) — wallet FIRST: Apple Pay / Google Pay mount here as
              the wallets' own native buttons; one press = the order. */}
          <div id="express-checkout-element" className="widget-wallets" hidden={props.wallets === undefined || props.wallets.length === 0} />
          {props.wallets !== undefined && props.wallets.length > 0 ? <LabelledRule label="or pay with card" /> : null}
          {/* B545 (1) — 356:10 heads the card field CARD (label 13, field at
              +20). The provider's tokenised element mounts here (§7 step 4) —
              fields live inside OUR origin's iframe, never the host DOM. */}
          <div className="widget-cardfield">
            <p className="widget-label" id="widget-card-label">
              Card
            </p>
            <div id="payment-element" aria-labelledby="widget-card-label" />
          </div>
          {props.gatewayReady || payWall === undefined ? null : (
            <Banner tone="info" id="widget-pay-gate">
              {payWall}
            </Banner>
          )}
          {/* B415: NAME above EMAIL (the widget's slice of the 73:2 amendment).
              enter-ok: B683 — the pay step's only door is PAY, a money verb Enter must never fire; it stays click-only. */}
          <Input
            id="widget-buyer-name"
            label="Name for the booking"
            value={props.buyerName}
            placeholder="Maya Chen"
            onChange={props.onBuyerName}
            error={props.nameRefusal}
          />
          <Input
            id="widget-buyer-email"
            label="Email for your tickets"
            type="email"
            value={props.buyerEmail}
            placeholder="you@example.com"
            onChange={props.onBuyerEmail}
            error={props.emailRefusal}
          />
          {/* B616 leg 3a — the Insider opt-in, unticked, never required (cartPay's OptInBox twin). */}
          {props.optIn === undefined ? null : (
            <Checkbox id="widget-buyer-optin" label={props.optIn} checked={props.optedIn === true} onChange={(ticked) => props.onOptIn?.(ticked)} />
          )}
          {/* B615 leg 2 — the acknowledgement, last before PAY (cartPay's AcknowledgeBox twin). */}
          {props.acknowledgement === undefined ? null : (
            <Checkbox
              id="widget-buyer-acknowledge"
              label={props.acknowledgement}
              checked={props.acknowledged === true}
              onChange={(ticked) => props.onAcknowledged?.(ticked)}
              {...(props.acknowledgeRefusal !== undefined ? { error: props.acknowledgeRefusal } : {})}
            />
          )}
          {payWall !== undefined && props.gatewayReady ? (
            <p className="t-gate-note" role="note" id="widget-pay-gate">
              {payWall}
            </p>
          ) : null}
          <Button
            size="lg"
            disabled={payWall !== undefined}
            busy={props.paying}
            {...(payWall !== undefined ? { describedBy: issues.length > 0 ? "widget-issues" : "widget-pay-gate" } : {})}
            onClick={props.onPay}
          >
            {total !== undefined ? `Pay ${formatMoney(total)}` : "Pay"}
          </Button>
          <p className="widget-trust" data-redline="widget-trust">
            <Icon name="lock" size={11} />{" "}
            {[
              ...(props.testMode ? ["Test mode"] : []),
              ...(props.gatewayReady ? ["Secure checkout"] : []),
              "Powered by transact",
            ].join(" · ")}
          </p>
        </div>
      )}

      {props.locked && props.onStartOver !== undefined ? (
        <p className="widget-startover">
          <Button variant="ghost" onClick={props.onStartOver}>
            Start over
          </Button>
        </p>
      ) : null}
    </SheetCard>
  );
}
